import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Menu, createStandardMenuItems } from "@msuite/ui/menu";
import { ConfirmModal, ModalWrapper, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { moment } from "@msuite/utils/date";
import { guardString } from "@msuite/utils/strings";
import React, { memo, useMemo } from "react";
import { client } from "@/hono";
import type { CrewDefinitionListItem } from "@/hooks/use-crew-definitions";
import { EditManagerSlotModal } from "@/modals/plan/edit-manager-slot-modal";
import {
	CapacityCalendarCells,
	CapacityCalendarRowWrapper,
	useCapacityCalendarContext,
} from "../capacity-calendar";
import { buildManagerSlotBarGradient, managerSlotColor } from "./bar-gradient";
import { CapacityPlannerBar } from "./capacity-planner-bar";
import { CapacityPlannerRowHead } from "./capacity-planner-row-head";

/** Props Interface */
interface CapacityPlannerManagerSlotRowProps {
	crewDefinition: CrewDefinitionListItem;
	managerSlot: CrewDefinitionListItem["_managerSlots"][number];
}

function CapacityPlannerManagerSlotRowImpl({
	crewDefinition,
	managerSlot,
}: CapacityPlannerManagerSlotRowProps) {
	/** Context */
	const { controller } = useCapacityCalendarContext();

	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const editModal = useDisclosure();
	const asyncModal = useAsyncModal();

	/** Functions */
	async function handleOnDelete() {
		const isConfirmed = await asyncModal.confirm();
		if (!isConfirmed) return;
		const response = await client.crew_definitions[":crewDefinitionId"]["manager-slots"][
			":managerSlotId"
		].$delete({
			param: { crewDefinitionId: crewDefinition.id, managerSlotId: managerSlot.id },
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["crew-definitions"]) });
	}

	/** Computed */
	const color = managerSlotColor(managerSlot.userId);

	const gradient = useMemo(() => {
		const covered = controller.cells.map((cell) => {
			const date = cell.cellMoment.db();
			if (date < managerSlot.startDate) return false;
			if (managerSlot.endDate && date > managerSlot.endDate) return false;
			return true;
		});
		return buildManagerSlotBarGradient(covered, color);
	}, [controller.cells, managerSlot.startDate, managerSlot.endDate, color]);

	// An open-ended window has no second date to span to, so it reads as its start and an infinity
	// rather than as a span of one day.
	const span = managerSlot.endDate
		? moment().getDateSpanString([managerSlot.startDate, managerSlot.endDate])
		: `${moment(managerSlot.startDate).dateFormat()} – ∞`;

	/** Render */
	return (
		<React.Fragment>
			<CapacityCalendarRowWrapper variant="sub">
				<CapacityPlannerRowHead
					actions={
						<Menu
							items={createStandardMenuItems({
								onEdit: editModal.onOpen,
								onDelete: handleOnDelete,
							})}
						>
							<Button
								variant="white"
								size="xxs"
								icon={<Icon name="EllipsisVertical" />}
							/>
						</Menu>
					}
				>
					<p className="text-sm truncate">
						{guardString(managerSlot.user?.name, t("planning.crews.manager_slot_manager"))}
					</p>
					<p className="label truncate">{span}</p>
				</CapacityPlannerRowHead>
				<CapacityCalendarCells />
				<CapacityPlannerBar gradient={gradient} />
			</CapacityCalendarRowWrapper>
			{editModal.isOpen && (
				<EditManagerSlotModal
					isOpen={editModal.isOpen}
					onClose={editModal.onClose}
					crewDefinitionId={crewDefinition.id}
					managerSlotId={managerSlot.id}
					crewStartDate={crewDefinition.startDate}
					crewEndDate={crewDefinition.endDate}
					defaultValues={{
						userId: managerSlot.userId,
						startDate: managerSlot.startDate,
						endDate: managerSlot.endDate,
					}}
				/>
			)}
			<ModalWrapper>
				{asyncModal.isOpen && (
					<ConfirmModal
						controller={asyncModal}
						message={t("confirmation.confirm_deletion")}
					/>
				)}
			</ModalWrapper>
		</React.Fragment>
	);
}

export const CapacityPlannerManagerSlotRow = memo(CapacityPlannerManagerSlotRowImpl);
